/**
 * PWA utility functions
 */

/**
 * Check if the device is mobile based on user agent
 * @returns {boolean} - True if the user is on a mobile device
 */
export function isMobileDevice() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Mobile device regex patterns
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  return mobileRegex.test(userAgent);
}

/**
 * Check if the app is running in standalone mode (installed PWA)
 * @returns {boolean} - True if running as installed PWA
 */
export function isRunningAsInstalledPwa() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone || // for iOS
    document.referrer.includes('android-app://')
  );
}

/**
 * Check if the user has recently dismissed the install prompt
 * @returns {boolean} - True if the user has dismissed the prompt within the last 7 days
 */
export function hasUserDismissedPrompt() {
  const dismissedAt = localStorage.getItem('pwa_prompt_dismissed');
  
  if (!dismissedAt) {
    return false;
  }
  
  const dismissedTime = parseInt(dismissedAt, 10);
  const now = Date.now();
  const sevenDays = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  
  return (now - dismissedTime) < sevenDays;
}

/**
 * Check if we should show the install prompt
 * @param {Event|null} installPromptEvent - The beforeinstallprompt event
 * @returns {boolean} - True if we should show the install prompt
 */
export function shouldPromptToInstall(installPromptEvent) {
  // Only show prompt if:
  // 1. User is on mobile
  // 2. App is not already installed/running in standalone mode
  // 3. Browser supports installation (installPromptEvent exists)
  // 4. User hasn't dismissed the prompt recently
  return (
    isMobileDevice() && 
    !isRunningAsInstalledPwa() && 
    installPromptEvent !== null &&
    !hasUserDismissedPrompt()
  );
}

/**
 * Diagnose PWA installation eligibility issues
 * Logs detailed information about why installation might not be working
 * @returns {Object} - Diagnostic information about PWA installation status
 */
export function diagnosePwaInstallation() {
  // Create diagnostic object
  const diagnostic = {
    isMobile: isMobileDevice(),
    isAlreadyInstalled: isRunningAsInstalledPwa(),
    hasDismissedPrompt: hasUserDismissedPrompt(),
    hasServiceWorker: 'serviceWorker' in navigator,
    hasManifest: false,
    hasSufficientIcons: false,
    problems: []
  };
  
  // Check if manifest link exists
  const manifestLink = document.querySelector('link[rel="manifest"]');
  diagnostic.hasManifest = !!manifestLink;
  
  if (!diagnostic.hasManifest) {
    diagnostic.problems.push('No web app manifest link found in HTML');
  }
  
  // Check if manifest has required properties
  fetch(manifestLink?.href || '/manifest.webmanifest')
    .then(response => {
      if (!response.ok) {
        diagnostic.problems.push(`Manifest file not found or not accessible: ${response.status}`);
        return null;
      }
      return response.json();
    })
    .then(manifest => {
      if (!manifest) return;
      
      // Check for required manifest properties
      if (!manifest.name) diagnostic.problems.push('Manifest missing "name" property');
      if (!manifest.short_name) diagnostic.problems.push('Manifest missing "short_name" property');
      if (!manifest.start_url) diagnostic.problems.push('Manifest missing "start_url" property');
      if (!manifest.display) diagnostic.problems.push('Manifest missing "display" property');
      
      // Check icons
      if (!manifest.icons || manifest.icons.length === 0) {
        diagnostic.problems.push('Manifest missing icons');
      } else {
        // Check for minimum 192px and 512px icons
        const has192 = manifest.icons.some(icon => icon.sizes?.includes('192x192'));
        const has512 = manifest.icons.some(icon => icon.sizes?.includes('512x512'));
        
        diagnostic.hasSufficientIcons = has192 && has512;
        
        if (!has192) diagnostic.problems.push('Manifest missing 192x192 icon');
        if (!has512) diagnostic.problems.push('Manifest missing 512x512 icon');
      }
      
      console.log('PWA Installation Diagnostic:', diagnostic);
    })
    .catch(error => {
      diagnostic.problems.push(`Error fetching manifest: ${error.message}`);
      console.log('PWA Installation Diagnostic:', diagnostic);
    });
    
  // Log initial diagnostic
  console.log('PWA Installation Diagnostic (Initial):', diagnostic);
  return diagnostic;
}

/**
 * Ensure the manifest link is present in the document head
 * @returns {boolean} - True if the manifest link is present or was added
 */
export function ensureManifestLink() {
  if (!document.querySelector('link[rel="manifest"]')) {
    try {
      console.log('Adding missing manifest link...');
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/manifest.webmanifest';
      document.head.appendChild(link);
      console.log('Manifest link added to document head');
      return true;
    } catch (error) {
      console.error('Error adding manifest link:', error);
      return false;
    }
  }
  console.log('Manifest link already exists');
  return true;
}

/**
 * Reset all PWA-related data and refresh the page
 * Use this as a last resort when PWA installation is not working
 */
export function resetPwaState() {
  try {
    // Clear local storage flags
    localStorage.removeItem('pwa_prompt_dismissed');
    sessionStorage.removeItem('installBannerClosed');
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        for (let registration of registrations) {
          registration.unregister();
          console.log('Service worker unregistered');
        }
      });
    }
    
    // Clear caches
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        return Promise.all(
          cacheNames.map(cacheName => {
            return caches.delete(cacheName);
          })
        );
      }).then(() => {
        console.log('Caches cleared');
      });
    }
    
    // Add manifest link if missing
    ensureManifestLink();
    
    console.log('PWA state reset successfully');
    return true;
  } catch (error) {
    console.error('Error resetting PWA state:', error);
    return false;
  }
} 